import { useDitto } from '@dittolive/react-ditto'
import type { DQLHTTPResponse } from '@portal/dql-editor'
import { useAppContext } from 'context'
import dayjs from 'dayjs'
import { useCallback, useEffect, useRef, useState } from 'react'

import {
  type LegacyFindParams,
  downloadAttachment,
  executeDqlQuery,
  executeLegacyFindByIdQuery,
  executeLegacyFindQuery,
  executeLegacyRemove,
  executeLegacyUpdate,
  executeLegacyUpsert,
  getCollectionDocumentCount,
  isEmptyCollection,
  LegacyFindByIdParams,
  LegacyFindByIdResponse,
  LegacyFindResponse,
  LegacyRemoveParams,
  LegacyUpdateParams,
  LegacyUpsertParams,
  rpcLogin,
} from '../../../utils'

/** Hook used by the portal data browser to interact with an app's RPC API. */
const useRpcClient = (appId: string, appDomain?: string) => {
  // We need to keep the access token in state for the return value of this hook,
  // and in a ref to avoid recreating the isEmptyCollectionThroughRpc callback
  // unnecessarily, so that it can be safely placed in dependency arrays.
  const [rpcAccessToken, setRpcAccessToken] = useState<string>()
  const rpcAccessTokenRef = useRef<string>()
  const { jwtToken } = useAppContext()
  const tokenRenewalTimeout = useRef<number>()
  const { ditto, error } = useDitto(appId)

  const renewRpcToken = async () => {
    if (ditto && appDomain) {
      const currentToken = await jwtToken()

      const providerParams: [string, string] =
        process.env.NODE_ENV === 'development'
          ? ['dummy-provider', 'full_access']
          : ['portal', currentToken]

      const { accessToken, tokenExpiration } = await rpcLogin(
        appDomain,
        appId,
        providerParams[0],
        providerParams[1],
      )

      const expiresIn = dayjs(tokenExpiration).diff()

      setRpcAccessToken(accessToken)
      rpcAccessTokenRef.current = accessToken

      tokenRenewalTimeout.current = window?.setTimeout(
        renewRpcToken,
        expiresIn - 60000,
      )
    }
  }

  useEffect(() => {
    renewRpcToken()

    return () => {
      if (tokenRenewalTimeout.current) {
        clearTimeout(tokenRenewalTimeout.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [!!ditto])

  const isEmptyCollectionThroughRpc = useCallback(
    (collectionName: string, query: string) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? isEmptyCollection(
            collectionName,
            query,
            appId,
            appDomain,
            accessToken,
          )
        : Promise.resolve(false)
    },
    [appId, appDomain],
  )

  const getCollectionDocumentCountThroughRpc = useCallback(
    (collectionName: string, query = 'true') => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? getCollectionDocumentCount(
            collectionName,
            query,
            appId,
            appDomain,
            accessToken,
          )
        : Promise.resolve(0)
    },
    [appId, appDomain],
  )

  const executeLegacyFindQueryThroughRpc = useCallback(
    (params: LegacyFindParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyFindQuery(params, appDomain, appId, accessToken)
        : Promise.resolve({
            documents: [],
          } as unknown as LegacyFindResponse)
    },
    [appId, appDomain],
  )

  const executeLegacyFindByIdQueryThroughRpc = useCallback(
    (params: LegacyFindByIdParams): Promise<LegacyFindByIdResponse | null> => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyFindByIdQuery(params, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const executeLegacyUpdateThroughRpc = useCallback(
    (params: LegacyUpdateParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyUpdate(params, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const executeLegacyUpsertThroughRpc = useCallback(
    (params: LegacyUpsertParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyUpsert(params, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const executeLegacyRemoveThroughRpc = useCallback(
    (params: LegacyRemoveParams) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeLegacyRemove(params, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const downloadAttachmentThroughRpc = useCallback(
    (id: string) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? downloadAttachment(id, appDomain, appId, accessToken)
        : Promise.resolve(null)
    },
    [appId, appDomain],
  )

  const executeDqlQueryThroughRpc = useCallback(
    (query: string, args?: Record<string, unknown>) => {
      const accessToken = rpcAccessTokenRef.current
      return accessToken && appDomain
        ? executeDqlQuery(query, appDomain, appId, accessToken, args)
        : Promise.resolve({
            queryType: 'unknown',
            items: [],
            error: {},
            warnings: [],
          } as DQLHTTPResponse)
    },
    [appId, appDomain],
  )

  return {
    rpcAccessToken,
    refreshToken: renewRpcToken,
    isEmptyCollection: isEmptyCollectionThroughRpc,
    getCollectionDocumentCount: getCollectionDocumentCountThroughRpc,
    downloadAttachment: downloadAttachmentThroughRpc,
    executeDqlQuery: executeDqlQueryThroughRpc,
    executeLegacyFindQuery: executeLegacyFindQueryThroughRpc,
    executeLegacyFindByIdQuery: executeLegacyFindByIdQueryThroughRpc,
    executeLegacyUpdate: executeLegacyUpdateThroughRpc,
    executeLegacyUpsert: executeLegacyUpsertThroughRpc,
    executeLegacyRemove: executeLegacyRemoveThroughRpc,
    error,
  }
}

export default useRpcClient
